// Welcome Bonus — badge + claim + quest popup.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Gift, Check, Info, BadgeCheck } from 'lucide-react';
import { Alert, Button, Checkbox, Dialog, StatusBadge, cx, focusRing } from '@/components/ui';
import VerifyAccountModal from '@/features/account/components/VerifyAccountModal';
import { apiService } from '@/services/apiService';
import { useSignupBonus } from './useSignupBonus';
import type { SignupBonus } from './signupBonusService';
import {
    claimBlockFallbackMessage,
    isBlockingClaimErrorCode,
    isSignupBonusOffered,
    needsContactVerification,
} from './signupBonusGating';
import {
    isWelcomePromptHidden,
    markWelcomePromptDismissed,
    setWelcomePromptHidden,
    shouldAutoOpenWelcomeBonus,
} from './welcomeBonusPrompt';
import { isExpiredBonusNoticeDue, markExpiredBonusRead, noteExpiredBonusSeen } from './expiredBonusNotice';

const money = (n: number) => `$${(Number(n) || 0).toFixed(2)}`;
const GAMES_ROUTE = '/account/games';

function verifyChannelsForMode(mode: SignupBonus['claimVerifyMode']): Array<'email' | 'phone'> {
    if (mode === 'email') return ['email'];
    if (mode === 'both') return ['email', 'phone'];
    return ['phone'];
}

/** Celebration pieces: the token action, payout, success and info colours. */
const CONFETTI_CLASSES = ['bg-action-payout', 'bg-action-primary', 'bg-success', 'bg-info', 'bg-amount-in'];

const Confetti: React.FC = () => {
    const pieces = Array.from({ length: 24 }, (_, i) => i);
    return (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
            {/* Name, easing and fill live in a class (not an inline `animation`) so the
                "Animations off" rule in globals.css can still stop it. */}
            <style>{`@keyframes sb-confetti-fall{0%{transform:translateY(-20px) rotate(0);opacity:1}100%{transform:translateY(320px) rotate(540deg);opacity:0}}.sb-confetti-piece{animation:sb-confetti-fall 1s ease-in forwards}`}</style>
            {pieces.map((i) => (
                <span
                    key={i}
                    className={cx('sb-confetti-piece absolute top-0 size-2', i % 2 ? 'rounded-full' : 'rounded-xs', CONFETTI_CLASSES[i % CONFETTI_CLASSES.length])}
                    style={{
                        left: `${(i * 4.3) % 100}%`,
                        animationDuration: `${1 + (i % 5) * 0.25}s`,
                        animationDelay: `${((i % 7) * 0.1).toFixed(2)}s`,
                    }}
                />
            ))}
        </div>
    );
};

const STATUS_LABEL: Record<SignupBonus['status'], string> = {
    inactive: 'GET BONUS',
    active: 'BONUS ACTIVE',
    in_review: 'IN REVIEW',
    completed: 'BONUS UNLOCKED',
    expired: 'BONUS EXPIRED',
};

/** Bonus status → the shared status map (`StatusBadge domain="bonus"`). */
const BADGE_STATUS: Record<SignupBonus['status'], string> = {
    inactive: 'active', // unclaimed = an invitation to claim, shown like an active bonus (was amber with active)
    active: 'active',
    in_review: 'in_progress',
    completed: 'completed',
    expired: 'expired',
};

const ProgressQuest: React.FC<{
    title: string;
    tooltip: string;
    current: number;
    target: number;
    met: boolean;
    actionLabel?: string;
    onAction?: () => void;
}> = ({ title, tooltip, current, target, met, actionLabel, onAction }) => {
    const pct = target > 0 ? Math.min(100, (current / target) * 100) : 0;
    return (
        <div className="flex w-full flex-col gap-3 rounded-container border border-edge-subtle bg-inset p-4">
            <div className="flex items-start gap-3">
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <div className="flex items-center gap-1.5">
                        <span className="font-ui text-small font-semibold text-primary">{title}</span>
                        <span className="group relative inline-flex">
                            <Info className="size-3.5 cursor-help text-tertiary" />
                            <span className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 w-56 -translate-x-1/2 rounded-control border border-edge bg-raised px-3 py-2 font-ui text-small text-secondary opacity-0 shadow-raised transition-opacity group-hover:opacity-100">
                                {tooltip}
                            </span>
                        </span>
                    </div>
                    {met ? (
                        <span className="inline-flex items-center gap-1.5 font-ui text-small text-success">
                            <span className="inline-flex size-4 items-center justify-center rounded-full bg-success/12">
                                <Check className="size-3" strokeWidth={3} />
                            </span>
                            Done
                        </span>
                    ) : (
                        <span className="font-money text-small tabular-nums text-secondary">
                            {money(current)} / {money(target)}
                        </span>
                    )}
                </div>
                {actionLabel && onAction && !met && (
                    <Button variant="primary" size="sm" silent onClick={onAction}>
                        {actionLabel}
                    </Button>
                )}
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-selected">
                <div
                    className={cx('h-full rounded-full transition-all duration-500', met ? 'bg-success' : 'bg-beam')}
                    style={{ width: `${met ? 100 : pct}%` }}
                />
            </div>
        </div>
    );
};

const QuestModal: React.FC<{
    bonus: SignupBonus;
    onClose: () => void;
    claiming: boolean;
    claimError: string | null;
    claimErrorCode?: string | null;
    onClaim: () => Promise<void>;
    onVerify: () => void;
}> = ({ bonus, onClose, claiming, claimError, claimErrorCode, onClaim, onVerify }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const goPlay = () => { onClose(); navigate(GAMES_ROUTE); };
    const grant = Number(bonus.grantedAmount) || 0;
    const liveNet = Number(bonus.netWin) || 0;
    const floorLiftedByLoss = bonus.floorLiftedByLoss === true || liveNet <= -grant;
    // Never offer an enabled button under a warning that says the claim cannot work: any block
    // reported by the status API, or a failed claim that another tap cannot fix, disables it.
    const claimBlocked = !!bonus.claimBlockCode || isBlockingClaimErrorCode(claimErrorCode);
    const mustVerify = needsContactVerification(bonus, claimErrorCode);
    const notice =
        claimError
        || bonus.claimBlockMessage
        || (bonus.claimBlockCode ? claimBlockFallbackMessage(bonus.claimBlockCode) : null);
    /** "Don't show this again": stops the sheet opening by itself on later visits (this account). */
    const [hidePrompt, setHidePrompt] = useState(() => isWelcomePromptHidden());

    return (
        <Dialog open onClose={onClose} size="sm" placement="center" title="Welcome Bonus" closeLabel={t('common.close')}>
            <div className="mb-5 flex flex-col items-center gap-1 text-center">
                <div className="mb-2 flex size-14 items-center justify-center rounded-container border border-action-primary/36 bg-action-primary/14 text-amount-in">
                    <Gift className="size-7" />
                </div>
                <h3 className="m-0 font-display text-title-lg font-bold text-primary">
                    {money(bonus.grantedAmount)} Welcome Bonus
                </h3>
                <p className="m-0 font-ui text-small text-secondary">
                    {bonus.status === 'inactive' && 'Claim your welcome bonus credits to start playing.'}
                    {bonus.status === 'active' && `Playing with ${money(bonus.grantedAmount)} bonus credits.`}
                    {bonus.status === 'in_review' && 'Quest complete — first payout needs manual approval.'}
                    {bonus.status === 'completed' && 'Welcome bonus settled — you can withdraw your full balance.'}
                    {bonus.status === 'expired' && 'This bonus expired after inactivity — grant credits were recalled.'}
                </p>
            </div>

            {bonus.status === 'inactive' && (
                <div className="flex flex-col gap-3">
                    {notice && (
                        <div data-testid="signup-bonus-claim-notice">
                            <Alert tone="warning">{notice}</Alert>
                        </div>
                    )}
                    {mustVerify ? (
                        <Button
                            variant="primary"
                            size="lg"
                            fullWidth
                            silent
                            icon={<BadgeCheck />}
                            onClick={onVerify}
                            data-testid="signup-bonus-verify-cta"
                        >
                            Verify and Get Bonus
                        </Button>
                    ) : (
                        <Button
                            variant="primary"
                            size="lg"
                            fullWidth
                            silent
                            disabled={claiming || claimBlocked}
                            onClick={() => { void onClaim(); }}
                            data-testid="signup-bonus-claim-cta"
                        >
                            {claiming ? 'Activating…' : 'Get Welcome Bonus'}
                        </Button>
                    )}
                    <p className="m-0 px-2 text-center font-ui text-small text-tertiary">
                        Credits come from your shop (or the platform). If they do not have enough credits, try again later.
                    </p>
                    <Checkbox
                        className="mx-auto"
                        checked={hidePrompt}
                        onChange={(checked) => {
                            setHidePrompt(checked);
                            setWelcomePromptHidden(checked);
                        }}
                        label="Don't show this again"
                        data-testid="signup-bonus-hide-prompt"
                    />
                </div>
            )}

            {bonus.status === 'active' && (
                <div className="flex flex-col gap-4">
                    {/* Event P&L — scoped to Welcome Bonus (since claim), not all-time */}
                    <div className="flex flex-col items-center gap-1 rounded-container border border-edge-subtle bg-inset px-4 py-4 text-center">
                        <p className="m-0 font-ui text-small font-medium text-tertiary">
                            Bonus event P&L
                        </p>
                        <p
                            className={cx(
                                'm-0 font-money text-display font-semibold tabular-nums',
                                liveNet > 0 ? 'text-success' : liveNet < 0 ? 'text-danger' : 'text-primary'
                            )}
                            data-testid="signup-bonus-live-pnl"
                        >
                            {liveNet > 0 ? '+' : ''}{money(liveNet)}
                        </p>
                        <p className="m-0 mt-1 font-ui text-small text-tertiary">
                            Cash out − sent to game, only since you claimed this bonus
                        </p>
                        <p className="m-0 font-ui text-small text-tertiary">
                            Not your all-time game P&L
                        </p>
                    </div>

                    {/* Event peak → In review */}
                    <ProgressQuest
                        title="Event peak unlock"
                        tooltip={`Hit ${money(bonus.winThreshold)} peak event P&L (highest since claim) to enter In review. Peak never goes down. This is not all-time P&L.`}
                        current={Math.max(0, Number(bonus.peakNetWin) || 0)}
                        target={bonus.winThreshold}
                        met={bonus.netWinMet}
                        actionLabel="Play"
                        onAction={goPlay}
                    />

                    {bonus.netWinMet ? (
                        <Alert tone="info" title="Peak reached">
                            Refreshing status… first payout will need manual approval (In review).
                        </Alert>
                    ) : floorLiftedByLoss ? (
                        <Alert tone="success" title="Floor lifted">
                            Event P&L ≤ −{money(grant).slice(1)} — withdraw freely until event P&L recovers above that.
                        </Alert>
                    ) : (
                        <Alert tone="warning" title={<>Keep ≥ {money(grant)} in wallet</>}>
                            Unlock at {money(bonus.winThreshold)} event peak to enter In review, or floor lifts at −{money(grant).slice(1)} event P&L.
                        </Alert>
                    )}
                </div>
            )}

            {bonus.status === 'in_review' && (
                <div className="flex flex-col gap-3">
                    <Alert tone="info" title="Event P&L quest complete">
                        No wallet floor. Every payout request must be approved manually
                        (shop for shop players, admin for platform players).
                        After the first approval, future payouts follow normal rules.
                    </Alert>
                    <ProgressQuest
                        title="Event peak unlock"
                        tooltip={`Hit ${money(bonus.winThreshold)} peak event P&L (highest since claim). Peak never goes down.`}
                        current={Math.max(0, Number(bonus.peakNetWin) || 0)}
                        target={bonus.winThreshold}
                        met
                    />
                </div>
            )}

            {bonus.status === 'completed' && (
                <div className="relative overflow-hidden rounded-container border border-success/28 bg-success/9 px-4 py-5 text-center">
                    <Confetti />
                    <div className="relative flex flex-col gap-1">
                        <p className="m-0 font-ui text-small font-semibold text-success">
                            Welcome bonus unlocked
                        </p>
                        <p className="m-0 font-ui text-small text-secondary">You can withdraw your full balance any time.</p>
                    </div>
                </div>
            )}

            {bonus.status === 'expired' && (
                <div className="rounded-control border border-edge bg-inset px-4 py-3 text-center">
                    <p className="m-0 font-ui text-small text-secondary">
                        This bonus expired after inactivity. Grant credits were recalled — withdrawals are no longer floored.
                    </p>
                </div>
            )}
        </Dialog>
    );
};

const SignupBonusBadge: React.FC = () => {
    const { bonus, claiming, claimError, claimErrorCode, setClaimError, claim, reload } = useSignupBonus();
    const [open, setOpen] = useState(false);
    const [verifyOpen, setVerifyOpen] = useState(false);
    const [contact, setContact] = useState<{
        email: string | null;
        phone: string | null;
        verified: Array<'email' | 'phone'>;
    }>({
        email: null,
        phone: null,
        verified: [],
    });

    // A Player with an unclaimed bonus sees the sheet each time they come in (page load or
    // sign-in), until they opt out. Closing it is what ends it for this visit.
    useEffect(() => {
        if (!shouldAutoOpenWelcomeBonus(bonus)) return;
        setOpen(true);
    }, [bonus]);

    const closeSheet = () => {
        markWelcomePromptDismissed();
        // Having read why the bonus expired, the Player does not need the chip any more.
        if (bonus?.status === 'expired') markExpiredBonusRead();
        setOpen(false);
    };

    // Start the "recently expired" clock for APIs that do not say when the bonus expired.
    useEffect(() => {
        if (bonus?.status === 'expired') noteExpiredBonusSeen();
    }, [bonus?.status]);

    useEffect(() => {
        if (bonus?.status !== 'in_review' && bonus?.status !== 'completed') return;
        const key = bonus.status === 'in_review' ? 'sb-in-review-opened' : 'sb-completed-celebrated';
        if (localStorage.getItem(key) === '1') return;
        localStorage.setItem(key, '1');
        setOpen(true);
    }, [bonus?.status]);

    if (!bonus) return null;
    // Bonus switched off for this player (shop opted out / platform off): advertise nothing. An
    // already-open modal stays until closed so a just-refused claim can show its reason.
    if (!open && !verifyOpen && !isSignupBonusOffered(bonus)) return null;
    // An expired bonus is news for a while, not a permanent label (KIOSK-459): once the Player has
    // read it, or a week after it expired, the header carries no bonus chip.
    if (!open && !isExpiredBonusNoticeDue(bonus)) return null;

    const badgeLabel = needsContactVerification(bonus, claimErrorCode)
        ? 'VERIFY AND GET BONUS'
        : STATUS_LABEL[bonus.status];

    const handleClaim = async () => {
        try {
            await claim();
        } catch {
            // claimError already set on hook
        }
    };

    // Swap the bonus modal for the verify modal, pre-filled with the contact already on the account.
    const handleVerify = async () => {
        closeSheet();
        try {
            const res = await apiService.getUserProfile();
            const u = (res.data as any)?.user as any;
            const verified: Array<'email' | 'phone'> = [];
            if (u?.email_verified === true) verified.push('email');
            if (u?.phone_verified === true) verified.push('phone');
            setContact({
                email: u?.email ?? null,
                phone: u?.phone ?? u?.phone_number ?? u?.profile?.phone ?? null,
                verified,
            });
        } catch {
            setContact({ email: null, phone: null, verified: [] });
        }
        setVerifyOpen(true);
    };

    const handleVerified = async () => {
        setClaimError(null);
        await reload();
        setOpen(true);
    };

    return (
        <>
            <button
                type="button"
                onClick={() => {
                    setClaimError(null);
                    setOpen(true);
                }}
                // A status pill in a 40px tap target; on phones only its gift icon shows. The label
                // stays on one line: when the header is short of room it is cut, not wrapped.
                className={cx('inline-flex h-10 min-w-10 items-center justify-center rounded-control px-1 transition-colors hover:bg-hover', focusRing)}
                aria-label="View welcome bonus"
                data-testid="signup-bonus-badge"
            >
                <StatusBadge
                    domain="bonus"
                    status={BADGE_STATUS[bonus.status]}
                    dot={false}
                    className="min-w-0 max-w-full"
                    label={
                        <>
                            <Gift aria-hidden className="size-3.5 shrink-0" />
                            <span className="hidden min-w-0 truncate @bp-sm:inline">{badgeLabel}</span>
                        </>
                    }
                />
            </button>
            {open && (
                <QuestModal
                    bonus={bonus}
                    onClose={closeSheet}
                    claiming={claiming}
                    claimError={claimError}
                    claimErrorCode={claimErrorCode}
                    onClaim={handleClaim}
                    onVerify={() => { void handleVerify(); }}
                />
            )}
            <VerifyAccountModal
                isOpen={verifyOpen}
                onClose={() => setVerifyOpen(false)}
                initialEmail={contact.email}
                initialPhone={contact.phone}
                allowedChannels={verifyChannelsForMode(bonus.claimVerifyMode)}
                verifiedChannels={contact.verified}
                requireAll={bonus.claimVerifyMode === 'both'}
                onVerified={() => { void handleVerified(); }}
            />
        </>
    );
};

export default SignupBonusBadge;
